import React, { useState, useMemo } from "react";
import Loader from "react-loader-spinner";
import { config } from "../../../../src/utils/conf";
import { doc_icon } from "../../../images";
import StepsCard from "./components/StepsCard/StepsCard";
import { PageDisable } from "../../../components";
import Button from "../../../components/Button/Button";
import ConfirmationModal from "../../../components/Modal/ConfirmationModal";

import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.css";
import "@uppy/dashboard/dist/style.css";

import useCreateBulkTransaction from "../../../hooks/Reporting/useBulkTransaction";
import { REPORTING_TRANSACTIONS_HISTORY } from "../../../components/Nav/Routes/Url";
import useDownloadArtifact from "../../../hooks/Order/useDownloadArtifact";
import _ from "lodash";
import useGetCustomer from "../../../hooks/CustomerProfile/useGetCustomer";
import useSession from "../../../hooks/Auth/useSession";
import "./style.css";

function DownloadArtifactLink({ url, fileName }) {
  const {
    data: bulkOrderFileData,
    status,
    isLoading,
  } = useDownloadArtifact({
    url: url,
  });
  const fileUrl = window.URL.createObjectURL(new Blob([bulkOrderFileData]));

  if (status === "error") return "Error while downloading the file";
  if (status === "loading") return "Loading...";

  return (
    <a href={fileUrl} download={fileName} className="tw-text-tosca-orange">
      {isLoading ? "loading..." : "Download"}
    </a>
  );
}

const BulkTransaction = (props) => {
  const { createBulkTransaction, createBulkTransactionStatus } =
    useCreateBulkTransaction();

  const { customerId } = useSession();
  const { data: customer } = useGetCustomer(customerId);
  const { data: bulkOrderFileData, isLoading } = useDownloadArtifact({
    url: `${config.inventoryApiUrl}/v1/transaction-templates`,
  });
  const fileUrl = window.URL.createObjectURL(new Blob([bulkOrderFileData]));

  const steps = [
    {
      id: 1,
      name: "PrepareData",
      title: "Download & Prepare Data",
    },
    {
      id: 2,
      name: "UploadData",
      title: "Upload Data & Confirmation",
    },
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(false);

  const duplicatedPoNumbersInResponse = [];
  const blankPoNumbersInResponse = [];

  const uppy = useMemo(() => {
    return new Uppy({
      autoProceed: false,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: [".xlsx"],
      },
    });
  }, []);

  uppy.on("file-added", () => {
    setSelectedFile(true);
  });

  uppy.on("file-removed", () => {
    setSelectedFile(false);
  });

  const handleChangeStep = (stepId) => {
    setCurrentStep(stepId);
  };

  const handelNextStep = () => {
    setCurrentStep((prevState) => prevState + 1);
  };

  const handelBackStep = () => {
    setCurrentStep((prevState) => prevState - 1);
  };

  const handelBulkOrderSubmit = (validations = true) => {
    const files = uppy.getFiles();
    if (!files.length) return;
    createBulkTransaction({ file: files[0].data, validations });
  };

  const showTimeoutMessage = createBulkTransactionStatus.isTimeout;

  const showErrorMessage =
    !showTimeoutMessage &&
    createBulkTransactionStatus.isError &&
    createBulkTransactionStatus?.error?.response?.status !== 409;

  const showDuplicatePoModal =
    createBulkTransactionStatus.isError &&
    createBulkTransactionStatus?.error?.response?.status === 409;

  return (
    <div>
      <PageDisable
        disabled={createBulkTransactionStatus.isLoading}
        message="Submitting bulk transactions, please wait"
      />

      <ConfirmationModal
        show={createBulkTransactionStatus.isSuccess}
        title="Request Submitted"
        onClose={() => createBulkTransactionStatus.reset()}
      >
        <p>Your transaction has been submitted.</p>
        <p>
          Please allow 4-5 minutes for new shipments to be displayed in
          Transaction History."
        </p>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-6">
          <Button
            brand="secondary"
            type="button"
            fullwidth="true"
            onClick={() => props.history.push(REPORTING_TRANSACTIONS_HISTORY)}
          >
            View Transactions
          </Button>
          <Button
            brand="primary"
            type="button"
            fullwidth="true"
            onClick={() => createBulkTransactionStatus.reset()}
          >
            Place Another Transaction
          </Button>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        show={showTimeoutMessage}
        brand="danger"
        title="Bulk Transaction Upload Processing"
        onClose={createBulkTransactionStatus.reset}
      >
        <p className="tw-text-justify">
          Thank you, your uploaded file is still processing. The submitted
          transactions should be available in Transaction History soon. Please
          do not resubmit.
        </p>
        <p className="tw-text-justify tw-break-words">
          If you have any questions or concerns, please contact{" "}
          <a
            className="tw-text-tosca-orange"
            href="mailto:AssetManagementTeam@toscaltd.com"
          >
            AssetManagementTeam@toscaltd.com
          </a>
        </p>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-6">
          <Button
            brand="secondary"
            fullwidth="true"
            type="button"
            onClick={createBulkTransactionStatus.reset}
          >
            OK
          </Button>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        show={false}
        title="Request Submitted"
        onClose={() => createBulkTransactionStatus.reset()}
      >
        <p>Your order has been submitted.</p>
        <p>
          * Please allow 1-2 minutes for new orders to be displayed in Order
          History.
        </p>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-6">
          <Button
            brand="secondary"
            type="button"
            fullwidth="true"
            onClick={() => props.history.push("/ordering")}
          >
            View Orders
          </Button>
          <Button
            brand="primary"
            type="button"
            fullwidth="true"
            onClick={() => createBulkTransactionStatus.reset()}
          >
            Place Another Order
          </Button>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        show={showErrorMessage}
        brand="danger"
        title={`${
          createBulkTransactionStatus?.error?.response?.status === 422
            ? "Validate Errors - Upload Failed"
            : "System Issue - Upload Failed"
        }`}
        onClose={createBulkTransactionStatus.reset}
      >
        {createBulkTransactionStatus?.error?.response?.status === 422 ? (
          <div>
            <p className="tw-break-normal">
              Some entries in the file are incorrect. Please download the
              updated file, make the necessary changes, and re-upload it.
            </p>
            {createBulkTransactionStatus?.error?.response?.data?.bulkSubmissionArtifacts?.map(
              (artifact) => (
                <div className="">
                  <DownloadArtifactLink
                    url={artifact.artifactWithErrorsRef}
                    fileName={`${_.snakeCase(
                      customer.customerName,
                    )}_bulk_transaction_upload_${
                      artifact.bulkSubmissionArtifactId
                    }.xlsx`}
                  />
                </div>
              ),
            )}
            <p className="tw-break-normal tw-mt-4">
              {" "}
              If any issue persists or other concerns arise, please contact
              Asset Management Team at&nbsp;
              <a
                href={`mailto:AssetManagementTeam@toscaltd.com&subject=Bulk Transaction Failure`}
              >
                AssetManagementTeam@toscaltd.com.
              </a>
            </p>
          </div>
        ) : (
          <>
            <p className="tw-break-normal">
              We were unable to complete the operation. Please make corrections
              to your file and try again to upload.
            </p>

            <p className="tw-break-normal tw-mt-4">
              If the issue persists, contact Asset Management at{" "}
              <a href="mailto:AssetManagementTeam@toscaltd.com">
                AssetManagementTeam@toscaltd.com
              </a>
            </p>
          </>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-6">
          <Button
            brand="secondary"
            fullwidth="true"
            type="button"
            onClick={createBulkTransactionStatus.reset}
          >
            {" "}
            OK{" "}
          </Button>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        show={showDuplicatePoModal}
        brand="primary"
        title="Duplicate Purchase Order Numbers Found"
        onClose={createBulkTransactionStatus.reset}
        size={"lg"}
      >
        {duplicatedPoNumbersInResponse.length > 0 && (
          <p className="tw-text-justify">
            The following Purchase Order Numbers are already in use.
            <br />
            <span className="">
              {duplicatedPoNumbersInResponse?.join(", ")}
            </span>
          </p>
        )}

        {blankPoNumbersInResponse.length > 0 && (
          <p className="tw-text-justify">
            {blankPoNumbersInResponse.length}{" "}
            {`${
              blankPoNumbersInResponse.length === 1
                ? "Order does not have PO Number entered."
                : "Orders do not have PO Numbers entered."
            }`}
          </p>
        )}

        <p className="tw-text-justify">Do you want to continue?</p>

        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 tw-mt-6">
          <Button
            brand="secondary"
            type="button"
            fullwidth="true"
            onClick={() => handelBulkOrderSubmit(false)}
          >
            YES
          </Button>
          <Button
            brand="primary"
            type="button"
            fullwidth="true"
            onClick={createBulkTransactionStatus.reset}
          >
            NO
          </Button>
        </div>
      </ConfirmationModal>

      <div className="trans-reporting-page-header">
        <div className="header-info">
          <h3>Bulk Transaction Reporting</h3>
          <p>Add transaction details as a bulk of your orders.</p>
        </div>
      </div>

      <StepsCard
        steps={steps}
        currentStep={currentStep}
        stepChange={handleChangeStep}
      >
        {/* Step - 01 */}
        {currentStep === 1 && (
          <>
            <div className="step-card-body">
              <p className="step-title">Prepare Data</p>

              <p className="step-title-2">
                Step 1 - Please download the given excel file
              </p>
              <div className="step-container">
                {isLoading ? (
                  <Loader
                    type="Oval"
                    color="rgba(246,130,32,1)"
                    height="50"
                    width="50"
                  />
                ) : (
                  <a
                    href={fileUrl}
                    download={`${_.snakeCase(
                      customer.customerName,
                    )}_bulk_transaction_upload.xlsx`}
                    className="download-btn"
                  >
                    <div className="iocn-wrapper">
                      <img
                        className="iocn"
                        src={doc_icon.xls}
                        alt="excel file"
                      />
                      <span className="iocn-caption">Template</span>
                    </div>
                    <p className="btn-discription">Download The Template</p>
                  </a>
                )}
              </div>

              <p className="step-title-2">
                Step 2 - Fill in the following fields: Ship From, Ship To,
                Transaction Date, Item ID, Quantity, Bill of Lading, and
                Purchase Order.
              </p>
              <div className="step-container">
                <img
                  className="tw-object-contain tw-max-w-full tw-max-h-full"
                  src={doc_icon.bulkTransactionSample}
                  alt="bulk data"
                />
              </div>

              <div className="step-footer lg:tw-mt-3">
                <div></div>
                <button className="next" onClick={handelNextStep}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step - 02 */}
        {currentStep === 2 && (
          <>
            <div className="step-card-body">
              <p className="step-title">Upload Data</p>

              <p className="step-title-2">
                Step 3 - Please upload the excel file that you have prepared
              </p>

              <div className="step-container-2 tw-relative">
                <Dashboard
                  width="100%"
                  height="100%"
                  uppy={uppy}
                  locale={{
                    strings: {
                      dropHereOr: "Drag and Drop your Excel File",
                      dropPasteFiles: "%{browseFiles}",
                    },
                  }}
                  hideUploadButton={true}
                />
              </div>
              <div className="step-footer">
                <button className="back" onClick={handelBackStep}>
                  Back
                </button>

                <button
                  disabled={!selectedFile}
                  className={`next ${
                    selectedFile ? "btn-enable" : "btn-disable"
                  }`}
                  onClick={handelBulkOrderSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
      </StepsCard>
    </div>
  );
};

export default BulkTransaction;
